<script setup lang="ts">
import { onBeforeUnmount, onMounted, useTemplateRef } from 'vue';
import { animations } from '@/services/Animations';

interface Props {
  isCreatingSession: boolean;
  samplePhotoUrl: string | null | undefined;
  samplePortraitUrl: string | null | undefined;
  startLabel: string;
  title: string;
}

interface PulseAnimation {
  stopImmediately?: () => void;
}

defineProps<Props>();

// biome-ignore lint/correctness/noUnusedVariables: used in <template>
const emit = defineEmits<{
  start: [];
}>();

const startButton = useTemplateRef<HTMLButtonElement>('startButton');
let pulseAnimation: PulseAnimation | null = null;

onMounted(() => {
  if (!startButton.value) {
    return;
  }
  pulseAnimation =
    animations.pulseBox(startButton.value, {
      infinite: true,
      gap: 400,
    }) ?? null;
});

onBeforeUnmount(() => {
  pulseAnimation?.stopImmediately?.();
  pulseAnimation = null;
});
</script>

<template>
  <h1 class="Home__title kiosk-title" data-testid="home-root">{{ title }}</h1>
  <div class="Home__sample text-center">
    <img
      v-show="samplePhotoUrl"
      class="Home__sample-img"
      data-testid="home-sample-photo"
      :src="samplePhotoUrl ?? undefined"
    />
    <img
      v-show="samplePortraitUrl"
      class="Home__sample-img"
      data-testid="home-sample-portrait"
      :src="samplePortraitUrl ?? undefined"
    />
  </div>

  <button
    ref="startButton"
    class="Home__start-button kiosk-button mx-auto"
    data-testid="home-start-button"
    :disabled="isCreatingSession"
    @click="emit('start')"
  >
    {{ startLabel }}
  </button>
</template>

<style scoped lang="scss">
.Home__title {
  margin-bottom: 3rem;
  max-width: 66.67rem;
  padding: var(--home-title-padding, 0);
}

.Home__sample {
  margin-bottom: 3rem;
}

.Home__sample-img {
  width: var(--home-sample-width, 19rem);
  height: var(--home-sample-height, 26rem);
  aspect-ratio: var(--home-sample-aspect-ratio, auto);
  object-fit: cover;
  border-radius: 1.11rem;
  box-shadow: -1.11rem 1.11rem 1.11rem -0.56rem
    rgba(var(--Kiosk--tint-color-rgb), 0.2);

  &:nth-child(even) {
    transform: rotate(-3deg) translateY(-0.44rem);
    margin-left: -0.56rem;
  }

  &:nth-child(odd) {
    transform: rotate(3deg);
    margin-right: -0.56rem;
  }
}

.Home__title,
.Home__sample,
.Home__start-button {
  position: relative;
  z-index: 1;
}
</style>
